// src/pages/dashboards/company/CompanyCodes.jsx
// Team onboarding. Issue a connect code that team members redeem once to link to
// the company, see/ revoke existing codes, and view the linked roster with each
// member's scan usage (so the company can see who consumes the shared pool).
// The backend resolves a company-role user to their company, so codes carry no
// company_id here.

import React, { useEffect, useState } from "react";
import * as companyApi from "../../../api/company";
import { Card, Button, Badge, Input, Field, Modal, Loading, EmptyState } from "../../../components/ui";
import { date, relative } from "../../../lib/format";
import { Plus, Users, Scan } from "../../../components/icons";
import { useToast } from "../../../context/ToastContext";

const codeState = (c) => {
  if (!c.is_active) return { tone: "gray", label: "revoked" };
  if (c.expires_at && new Date(c.expires_at) < new Date()) return { tone: "gray", label: "expired" };
  if (c.max_uses != null && c.used_count >= c.max_uses) return { tone: "gray", label: "used up" };
  return { tone: "green", label: "active" };
};

// initials avatar from a name
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

export default function CompanyCodes() {
  const toast = useToast();
  const [codes, setCodes] = useState(null);
  const [users, setUsers] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadCodes = () => companyApi.listCodes().then(setCodes).catch((e) => { toast.error(e.message); setCodes([]); });
  const loadUsers = () => companyApi.linkedUsers().then(setUsers).catch((e) => { toast.error(e.message); setUsers([]); });

  useEffect(() => { loadCodes(); loadUsers(); }, []);

  const revoke = async (id) => {
    try {
      await companyApi.revokeCode(id);
      toast.success("Code revoked");
      loadCodes();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success("Code copied"),
      () => toast.error("Could not copy")
    );
  };

  // scan stats across the roster (for the relative bar + total)
  const scanMax = Math.max(1, ...((users || []).map((u) => u.scan_count || 0)));
  const scanTotal = (users || []).reduce((s, u) => s + (u.scan_count || 0), 0);

  return (
    <div className="space-y-8">
      {/* connect codes */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-display font-bold">Team</h1>
          <Button onClick={() => setShowNew(true)}><Plus size={16} /> New code</Button>
        </div>

        {!codes ? (
          <Loading label="Loading codes" />
        ) : codes.length === 0 ? (
          <Card className="p-2">
            <EmptyState title="No connect codes yet" hint="Create one and share it with your team to link them." />
          </Card>
        ) : (
          <Card className="divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
            {codes.map((c) => {
              const st = codeState(c);
              return (
                <div key={c.id} className="px-4 sm:px-5 py-4 flex items-center gap-4">
                  <button
                    onClick={() => copy(c.code)}
                    title="Copy code"
                    className="font-mono text-lg tracking-widest font-bold hover:text-tb-brand transition-colors"
                  >
                    {c.code}
                  </button>
                  <div className="min-w-0 flex-1 text-sm text-tb-muted">
                    <span>{c.used_count}{c.max_uses != null ? ` / ${c.max_uses}` : ""} used</span>
                    {c.expires_at && <span> · expires {date(c.expires_at)}</span>}
                  </div>
                  <Badge tone={st.tone}>{st.label}</Badge>
                  {st.label === "active" && (
                    <Button variant="ghost" onClick={() => revoke(c.id)}>Revoke</Button>
                  )}
                </div>
              );
            })}
          </Card>
        )}
      </section>

      {/* linked members + scan usage */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-tb-muted" />
            <h2 className="text-lg font-display font-bold">Linked members</h2>
          </div>
          {users && users.length > 0 && (
            <span className="text-xs text-tb-muted flex items-center gap-1.5">
              <Scan size={14} /> {scanTotal} scan{scanTotal !== 1 ? "s" : ""} this term
            </span>
          )}
        </div>

        {!users ? (
          <Loading label="Loading members" />
        ) : users.length === 0 ? (
          <Card className="p-2">
            <EmptyState title="No members linked yet" hint="Share a connect code to get your team on board." />
          </Card>
        ) : (
          <Card className="divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
            {users.map((u) => {
              const scans = u.scan_count || 0;
              const pct = Math.round((scans / scanMax) * 100);
              return (
                <div key={u.link_id} className="px-4 sm:px-5 py-3.5 flex items-center gap-4">
                  {/* avatar */}
                  <div className="w-9 h-9 rounded-full shrink-0 grid place-items-center text-sm font-semibold text-tb-brand bg-tb-brand/10">
                    {initials(u.name)}
                  </div>

                  {/* name + email */}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{u.name}</p>
                    <p className="text-sm text-tb-muted truncate">{u.email}</p>
                  </div>

                  {/* scan usage: count + relative bar */}
                  <div className="hidden sm:block w-32 shrink-0">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-tb-muted">scans</span>
                      <span className="font-semibold tabular-nums">{scans}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-tb-bg-2 dark:bg-tb-d-inset overflow-hidden">
                      <div className="h-full rounded-full bg-tb-brand transition-all" style={{ width: `${Math.max(scans ? 4 : 0, pct)}%` }} />
                    </div>
                  </div>

                  {/* scan count on mobile (no bar) */}
                  <div className="sm:hidden text-right shrink-0">
                    <p className="font-semibold tabular-nums leading-none">{scans}</p>
                    <p className="text-[10px] text-tb-muted">scans</p>
                  </div>

                  <span className="hidden lg:block text-xs text-tb-muted shrink-0 w-24 text-right">linked {relative(u.linked_at)}</span>
                </div>
              );
            })}
          </Card>
        )}
      </section>

      <NewCodeModal open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadCodes(); }} />
    </div>
  );
}

// ---- new code modal -----------------------------------------------------
function NewCodeModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [maxUses, setMaxUses] = useState("");
  const [expiresDays, setExpiresDays] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setMaxUses(""); setExpiresDays(""); } }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const body = {};
      if (maxUses) body.max_uses = parseInt(maxUses, 10);
      if (expiresDays) body.expires_in_days = parseInt(expiresDays, 10);
      await companyApi.issueCode(body);
      toast.success("Code created");
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New connect code">
      <div className="space-y-4">
        <Field label="Max uses" hint="Leave blank for unlimited.">
          <Input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Unlimited" />
        </Field>
        <Field label="Expires in (days)" hint="Leave blank for no expiry.">
          <Input type="number" min="1" max="365" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} placeholder="Never" />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={create} disabled={busy}>Create code</Button>
        </div>
      </div>
    </Modal>
  );
}